import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { createGlobalStyle } from "styled-components";
import { ConfigProvider, App as AntApp } from "antd";
import theme from "./themes";
import Login from "./pages/auth/Login";
import VerificationCode from "./pages/auth/VerificationCode";
import CompanySelection from "./pages/auth/CompanySelection";
import Dashboard from "./pages/dashboard/Dashboard";
import store, { RootState, useAppSelector, useAppDispatch } from "./store";
import UserManagement from "./pages/user/UserManagement";
import MainLayout from "./layouts/MainLayout";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import Agents from "./pages/agents/Agents";
import AgentDetail from "./pages/agents/AgentDetail";
import Outbound from "./pages/outbound/Outbound";
import Mail from "./pages/inbound/Mail";
import MailDetail from "./pages/inbound/MailDetail";
import Whatsapp from "./pages/inbound/Whatsapp";
import Calls from "./pages/inbound/Calls";
import ReportPage from "./pages/reports/Report";
import ReportDetail from "./pages/reports/ReportDetail";
import Profile from "./pages/profile/Profile";
import CampaignDetailPage from "./pages/outbound/CampaignDetailPage";
import Settings from "./pages/settings/Settings";
import ConferencePage from "./pages/conference/Conference";
import { ROLES } from "./models/user.model";
import GoogleCallback from "./pages/auth/GoogleCallback";
import { getUserInfor } from "./store/actions/auth.action";
import { getCompanyInfoThunk } from "./store/actions/company.action";
import { initializeLanguageFromFreshData } from "./utils/languageUtils";
import i18n from "./i18n";
import { useChannelSettings } from "./hooks/useChannelSettings";
import { ChannelKey } from "./models/company.model";

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;
  }

  html, body, #root {
    max-width: 100%;
    min-width: 0;
    overflow-x: clip;
  }

  body {
    font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: #F9FAFB;
  }

  img, svg, video, canvas, iframe {
    max-width: 100%;
  }

  input, select, textarea {
    max-width: 100%;
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-input-number,
  .ant-select,
  .ant-picker,
  .ant-mentions {
    max-width: 100%;
  }

  .ant-card,
  .ant-row,
  .ant-col {
    min-width: 0;
  }

  .ant-space {
    max-width: 100%;
    flex-wrap: wrap !important;
  }

  .ant-form-item-label > label {
    white-space: normal !important;
  }

  .ant-modal {
    max-width: calc(100vw - 24px) !important;
  }

  .ant-modal .ant-modal-content {
    max-height: calc(100dvh - 32px);
    overflow: auto;
  }

  .ant-drawer-content-wrapper {
    max-width: 100vw !important;
  }

  .ant-picker-dropdown,
  .ant-select-dropdown,
  .ant-dropdown {
    max-width: calc(100vw - 16px);
  }

  table {
    text-align: left;
  }

  th {
    text-align: inherit;
  }

  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    vertical-align: middle;
  }

  .ant-table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  @media (max-width: 640px) {
    .ant-modal {
      margin: 12px auto !important;
      top: 12px;
      padding-bottom: 0;
    }

    .ant-modal-footer {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      justify-content: flex-end;
    }

    .ant-pagination {
      flex-wrap: wrap;
      justify-content: center;
      row-gap: 8px;
    }

    .ant-form-item .ant-form-item-label,
    .ant-form-item .ant-form-item-control {
      flex: 0 0 100% !important;
      max-width: 100% !important;
    }

    .ant-form-item .ant-form-item-label {
      padding-bottom: 4px;
      text-align: left !important;
    }

    .ant-modal .ant-col {
      max-width: 100% !important;
      flex: 0 0 100% !important;
    }

    .ant-card-head {
      flex-wrap: wrap;
      gap: 8px;
    }

    .ant-card-extra {
      margin-inline-start: 0 !important;
      width: 100%;
    }
  }

  /* Override Ant Design font family */
  .ant-btn,
  .ant-input,
  .ant-select,
  .ant-table,
  .ant-modal,
  .ant-card,
  .ant-form,
  .ant-typography,
  .ant-dropdown,
  .ant-menu,
  .ant-tabs,
  .ant-pagination,
  .ant-spin,
  .ant-message,
  .ant-notification,
  .ant-tooltip,
  .ant-popover,
  .ant-drawer,
  .ant-skeleton,
  .ant-alert,
  .ant-badge,
  .ant-tag,
  .ant-progress,
  .ant-slider,
  .ant-switch,
  .ant-checkbox,
  .ant-radio,
  .ant-rate,
  .ant-upload,
  .ant-transfer,
  .ant-tree,
  .ant-cascader,
  .ant-date-picker,
  .ant-time-picker,
  .ant-calendar,
  .ant-picker,
  .ant-mentions,
  .ant-auto-complete,
  .ant-cascader-picker,
  .ant-tree-select,
  .ant-mentions-dropdown,
  .ant-select-dropdown,
  .ant-cascader-menus,
  .ant-picker-dropdown,
  .ant-calendar-picker-container,
  .ant-time-picker-panel,
  .ant-mentions-dropdown-menu,
  .ant-select-tree,
  .ant-select-tree-dropdown,
  .ant-cascader-dropdown,
  .ant-tree-dropdown,
  .ant-mentions-dropdown-menu-item,
  .ant-select-item,
  .ant-select-tree-node-content-wrapper,
  .ant-cascader-menu-item,
  .ant-tree-node-content-wrapper,
  .ant-mentions-dropdown-menu-item-active,
  .ant-select-item-option-active,
  .ant-select-tree-node-content-wrapper:hover,
  .ant-cascader-menu-item:hover,
  .ant-tree-node-content-wrapper:hover,
  .ant-mentions-dropdown-menu-item-selected,
  .ant-select-item-option-selected,
  .ant-select-tree-node-content-wrapper.ant-select-tree-node-selected,
  .ant-cascader-menu-item-active,
  .ant-tree-node-content-wrapper.ant-tree-node-selected {
    font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif !important;
  }

  button {
    font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif !important;
  }

  .ant-alert-warning {
    background: ${theme.colors.yellow3};
    border: 1px solid rgba(253, 196, 72, 0.42);
    border-radius: 8px;

    .ant-alert-icon {
      color: ${theme.colors.yellow4};
    }

    .ant-alert-message {
      color: #3d3420;
      font-weight: 600;
    }

    .ant-alert-description {
      color: #5c4a1f;
    }
  }
`;

const RequireChannel: React.FC<{
  channel: ChannelKey;
  children: React.ReactElement;
}> = ({ channel, children }) => {
  const { isEnabled, loaded } = useChannelSettings();
  if (!loaded) return null;
  if (!isEnabled(channel)) return <Navigate to="/inbound" replace />;
  return children;
};

const AppContent = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isRequestedLogin, user } = useAppSelector(
    (state: RootState) => state.auth
  );

  useEffect(() => {
    const initializeApp = async () => {
      if (isAuthenticated) {
        try {
          // Fetch fresh user data first
          await dispatch(getUserInfor()).unwrap();

          // Then initialize language from fresh data
          initializeLanguageFromFreshData(i18n);
        } catch (error) {
          console.error("Failed to get user info:", error);
          // Fallback to localStorage language if API fails
          initializeLanguageFromFreshData(i18n);
        }
        dispatch(getCompanyInfoThunk());
      }
    };

    initializeApp();
  }, [dispatch, isAuthenticated, user?.company_id]);

  return (
    <Router>
      <GlobalStyle />
      <Routes>
        {!isAuthenticated && (
          <>
            <Route path="/" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
          </>
        )}
        {isAuthenticated && (
          <Route element={<MainLayout />}>
            <Route path="/" element={<Dashboard />} />
            {JSON.parse(localStorage.getItem("user") || "{}")?.role ===
              ROLES.ADMIN && (
              <Route path="/user-management" element={<UserManagement />} />
            )}
            <Route path="/agents" element={<Agents />} />
            <Route path="/agents/:id" element={<AgentDetail />} />
            <Route path="/outbound" element={<Outbound />} />
            <Route element={<Whatsapp />}>
              <Route path="/inbound" element={null} />
              <Route path="/inbound/whatsapp" element={null} />
              <Route path="/inbound/whatsapp/:id" element={null} />
            </Route>
            <Route
              path="/inbound/mail"
              element={
                <RequireChannel channel="email">
                  <Mail />
                </RequireChannel>
              }
            />
            <Route
              path="/inbound/mail/:id"
              element={
                <RequireChannel channel="email">
                  <MailDetail />
                </RequireChannel>
              }
            />
            <Route
              path="/inbound/calls"
              element={
                <RequireChannel channel="call">
                  <Calls />
                </RequireChannel>
              }
            />
            <Route path="/conference" element={<ConferencePage />} />
            <Route path="/reports" element={<ReportPage />} />
            <Route path="/reports/:id" element={<ReportDetail />} />
            <Route path="/outbound/:id" element={<CampaignDetailPage />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<Settings />} />
            <Route
              path="/settings/manage-phone-number"
              element={<Navigate to="/settings" replace />}
            />
            <Route
              path="/settings/assign-agents"
              element={<Navigate to="/settings" replace />}
            />
          </Route>
        )}
        {isAuthenticated && user?.is_super_admin && (
          <Route path="/company-selection" element={<CompanySelection />} />
        )}
        {isRequestedLogin && (
          <Route path="/verify" element={<VerificationCode />} />
        )}
        <Route path="/oauth2/callback/google" element={<GoogleCallback />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

const App = () => {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#109478",
          colorWarning: theme.colors.yellow4,
          colorWarningBg: theme.colors.yellow3,
          colorWarningBorder: "rgba(253, 196, 72, 0.42)",
          fontFamily:
            "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
        },
      }}
    >
      <AntApp>
        <AppContent />
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
